import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

interface PaginationProps {
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const PaginationContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0',
  marginTop: '6px',
  borderTop: '1px solid #EAECF0',
});

const PaginationControls = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const StyledSelect = styled(Select)({
  height: '31px',
  borderRadius: '8px',
  '& .MuiSelect-select': {
    padding: '8px 14px',
    paddingRight: '32px !important',
    fontSize: '12px',
  },
});

const PageInfo = styled(Typography)({
  color: '#667085',
  margin: '0 16px',
});

const CustomPagination: React.FC<PaginationProps> = ({
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalCount);

  const handleRowsPerPageChange = (
    event: SelectChangeEvent<unknown>,
    child: React.ReactNode
  ) => {
    onRowsPerPageChange(Number(event.target.value));
  };

  return (
    <PaginationContainer>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="#667085">
          Items per page:
        </Typography>
        <StyledSelect
          value={rowsPerPage}
          onChange={handleRowsPerPageChange}
          size="small"
          sx={{ ml: 1, mr: 2 }}
          IconComponent={KeyboardArrowDownIcon}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </StyledSelect>
        <PageInfo variant="body2">
          {startItem.toLocaleString()}-{endItem.toLocaleString()} of {totalCount.toLocaleString()} items
        </PageInfo>
      </Box>
      <PaginationControls>
        <IconButton
          onClick={() => onPageChange(0)}
          disabled={page === 0}
          size="small"
          title='First Page'
        >
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <IconButton
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          size="small"
          title='Previous Page'
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
        <PageInfo variant="body2">
          Page {(page + 1).toLocaleString()} of {totalPages.toLocaleString()}
        </PageInfo>
        <IconButton
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          size="small"
          title='Next Page'
        >
          <KeyboardArrowRightIcon />
        </IconButton>
        <IconButton
          onClick={() => onPageChange(totalPages - 1)}
          disabled={page >= totalPages - 1}
          size="small"
          title='Last Page'
        >
          <KeyboardDoubleArrowRightIcon />
        </IconButton>
      </PaginationControls>
    </PaginationContainer>
  );
};

export default CustomPagination; 